import EditIcon from '@mui/icons-material/Edit'
import SpeakerIcon from '@mui/icons-material/VolumeUp'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { styled } from '@mui/material/styles'
import React, { useEffect, useMemo, useState } from 'react'

import AudioPlayer from '@/app/component/atom/audio-player'
import { SearchableSelect } from '@/app/component/atom/form'
import Tooltip from '@/app/component/atom/tooltip'
import Message from '@/app/component/send/message'
import { useDebouncedFn } from '@/app/helpers'
import { CAMPAIGN_CHANGE_DELAY } from '@/app/module/campaigns/definitions'
import { BaseLanguageType } from '@/app/module/campaigns/helpers'
import { useCreateSpeechPreview } from '@/app/module/campaigns/store/ts/hooks'
import { PersonalizationType, SelectOption } from '@/app/types'

type Props = {
  activeLanguage?: string
  autoFocus?: boolean
  debounceChange?: boolean
  message: string
  personalizationList?: PersonalizationType
  selectedVoice: string
  voiceList: BaseLanguageType[]

  onChange: (message: any) => void
  onVoiceChange: (voice: string) => void
}

const CampaignContentTTS: React.FC<Props> = ({
  activeLanguage,
  autoFocus,
  debounceChange = true,
  message,
  personalizationList = {},
  selectedVoice,
  voiceList,
  onChange,
  onVoiceChange,
}) => {
  const [localMessage, setLocalMessage] = useState(message)
  const [previewing, setPreviewing] = useState(false)

  const [createSpeechPreview, { data, isLoading }] = useCreateSpeechPreview()

  const voiceOptions = useMemo(
    () => [
      ...voiceList.map((voice) => ({
        label: voice.name,
        value: voice.value,
      })),
    ],
    [voiceList],
  )

  const icon = useMemo(() => {
    if (isLoading) {
      return <CircularProgress size={20} />
    }

    if (previewing) {
      return <EditIcon />
    }

    return <SpeakerIcon />
  }, [isLoading, previewing])

  const buttonDisabled = (!previewing && (!message || !selectedVoice)) || isLoading

  const previewUrl = useMemo(() => data && URL.createObjectURL(data), [data])
  const displayPreview = previewing && !isLoading && !!previewUrl

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset previewing state when active language changes
  useEffect(() => {
    setPreviewing((s) => {
      if (s) {
        return false
      }
      return s
    })
  }, [activeLanguage])

  useEffect(() => {
    setLocalMessage((s) => {
      if (s !== message) {
        return message
      }
      return s
    })
  }, [message])

  const itemRef = React.useRef<{ message: string } | null>(null)
  const onChangeRef = React.useRef(onChange) // using ref so I can ignore it in the useEffect's deps below

  useEffect(
    () => () => {
      if (itemRef.current) {
        onChangeRef.current?.(itemRef.current)
      }
    },
    [],
  )

  const change = React.useCallback(
    (value: { message: string }) => {
      onChange(value)
      itemRef.current = null
    },
    [onChange],
  )
  const debouncedOnChange = useDebouncedFn(change, CAMPAIGN_CHANGE_DELAY)
  const handleTextChange = React.useCallback(
    (value: { message: string }) => {
      setLocalMessage(value.message)
      if (debounceChange) {
        itemRef.current = value
        debouncedOnChange(value)
      } else {
        onChange(value)
      }
    },
    [debounceChange, debouncedOnChange, onChange],
  )
  const handlePreviewToggle = React.useCallback(async () => {
    if (previewing) {
      setPreviewing(false)
      return
    }
    const success = await createSpeechPreview(selectedVoice, localMessage)
    setPreviewing(success)
  }, [localMessage, previewing, selectedVoice, createSpeechPreview])

  const handleVoiceChange = React.useCallback((value: string) => onVoiceChange(value), [onVoiceChange])

  return (
    <div data-testid="campaign-content-text-to-speech">
      <SearchableSelect
        className="tts-voice"
        disabled={previewing}
        hideRemoveSelection={!selectedVoice}
        label="Select voice to speak your message"
        showAll
        value={selectedVoice}
        options={voiceOptions}
        onChange={handleVoiceChange}
        keyPropFn={(option: SelectOption<string>) => option.value}
        valuePropFn={(option: SelectOption<string>) => option.label}
      />
      <Message
        additionalButtons={
          <Row>
            <Tooltip title={previewing ? 'Edit' : 'Play'}>
              <div>
                <PlayButton
                  color="primary"
                  data-testid="tts-play-button"
                  disabled={buttonDisabled}
                  variant="outlined"
                  onClick={handlePreviewToggle}
                >
                  {icon}
                </PlayButton>
              </div>
            </Tooltip>
            {displayPreview && <AudioPlayer playId={previewUrl} source={previewUrl} />}
          </Row>
        }
        autoFocus={autoFocus}
        editable={!previewing}
        error=""
        hideInfo
        label="Message"
        message={localMessage}
        personalizationList={personalizationList}
        onChange={handleTextChange}
      />
    </div>
  )
}

const PlayButton = styled(Button)({
  borderRadius: '50%',
  height: '40px',
  minWidth: 0,
  padding: 0,
  width: '40px',
})

const Row = styled('div')({
  display: 'flex',
  flexDirection: 'row',
})

export default CampaignContentTTS
